import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listFeedbackGoalChoices, ApiError } from '@/lib/api';
import { feedbackGoalChoicesKey } from '@/lib/queryClient';
import type { FeedbackDraftScope } from '@/lib/types';
import { useFeedbackAuthoringBoundary } from './useFeedbackAuthoringScope';

export function useFeedbackGoalChoices(scope: FeedbackDraftScope, playerIds: readonly string[],
  active: boolean, limit: number, cursor?: string) {
  const denied = useRef(false);
  const boundary = useFeedbackAuthoringBoundary(scope);
  const eligible = playerIds.length > 0 && playerIds.length <= 50 && new Set(playerIds).size === playerIds.length;
  const query = { player_ids: [...playerIds], limit, cursor };
  const result = useQuery({ queryKey: eligible ? feedbackGoalChoicesKey(scope, query) : ['feedback-goal-choices-disabled', scope],
    queryFn: async ({ signal }) => {
      let page;
      try { page = await listFeedbackGoalChoices(query, { scope, signal }); }
      catch (cause) { if (cause instanceof ApiError && [401, 403, 404].includes(cause.status)) denied.current = true; throw cause; }
      if (!boundary.current()) throw new DOMException('Feedback context changed', 'AbortError');
      return page;
    }, enabled: boundary.active && !denied.current && active && eligible, staleTime: 0, retry: false, refetchOnWindowFocus: false });
  return { ...result, eligible, authorized: boundary.active && !denied.current, denied: denied.current,
    page: boundary.active && !denied.current && active && eligible && result.isFetchedAfterMount && !result.isFetching && result.isSuccess ? result.data : null };
}
